// ---------------------------------------------------------------------------
// 테마 상태의 단일 소스. 테마는 두 축으로 나뉜다:
//  - 밝기 모드(appearance): system/light/dark. "system"은 OS 설정을 따른다.
//  - 색상 팔레트(theme): default/sepia/midnight/... — 각각 라이트·다크 버전을 가진다.
// 선호값은 localStorage 에 저장하고, DOM 에는 항상 해석된 값만 반영한다:
//   <html data-appearance="light|dark" data-theme="팔레트">
// CSS 쪽 정의는 apps/ui/src/styles/globals.css, 첫 페인트 전 적용은
// apps/app/index.html 의 인라인 스크립트가 담당한다(로직 변경 시 같이 수정).
// 서버(user_preference) 동기화는 queries/user-preferences/query.ts 참고.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from "react";
import {
  APPEARANCE_PREFERENCES,
  THEME_PALETTES,
  type AppearancePreference,
  type ThemePalette,
} from "shared/features/preferences";
import { getDesktopBridge } from "./Desktop";

// 값 목록의 정본은 shared/features/preferences — 서버 검증·drizzle 스키마와 같은 배열을
// 본다. 이 모듈에서 가져다 쓰던 소비처가 많아 그대로 재노출한다.
export type { AppearancePreference, ThemePalette };
export { APPEARANCE_PREFERENCES, THEME_PALETTES };

export const APPEARANCE_STORAGE_KEY = "appearance";
export const PALETTE_STORAGE_KEY = "palette";

export function isAppearancePreference(value: unknown): value is AppearancePreference {
  return APPEARANCE_PREFERENCES.includes(value as AppearancePreference);
}

export function isThemePalette(value: unknown): value is ThemePalette {
  return THEME_PALETTES.includes(value as ThemePalette);
}

const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // localStorage 접근 불가(사생활 보호 모드 등)면 기본값으로 동작
    return null;
  }
}

export function getAppearancePreference(): AppearancePreference {
  const stored = readStorage(APPEARANCE_STORAGE_KEY);
  return isAppearancePreference(stored) ? stored : "system";
}

export function getThemePalette(): ThemePalette {
  const stored = readStorage(PALETTE_STORAGE_KEY);
  return isThemePalette(stored) ? stored : "rose";
}

/** "system" 선호값을 OS 설정에 따라 light/dark 로 해석한다. */
function resolveAppearance(preference: AppearancePreference): "light" | "dark" {
  if (preference !== "system") return preference;
  return darkQuery.matches ? "dark" : "light";
}

function applyToDom() {
  document.documentElement.dataset.appearance = resolveAppearance(getAppearancePreference());
  document.documentElement.dataset.theme = getThemePalette();
  syncThemeColorMeta();
}

/** Safari 브라우저 크롬 등이 참고하는 theme-color 메타태그와, Electron 창 배경/
 *  titleBarOverlay 색을 현재 테마 배경/전경색에 맞춘다. */
function syncThemeColorMeta() {
  const style = getComputedStyle(document.documentElement);
  const background = style.getPropertyValue("--color-background").trim();
  const foreground = style.getPropertyValue("--color-foreground").trim();

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta && background) meta.setAttribute("content", background);

  if (background && foreground) {
    getDesktopBridge()?.updateThemeColors(background, foreground);
  }
}

const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // 저장 실패해도 이번 세션 동안은 적용은 유지
  }
}

/** 밝기 모드 선호값을 저장하고 즉시 DOM 에 반영한다. 서버 저장은 호출부 책임. */
export function setAppearancePreference(preference: AppearancePreference) {
  writeStorage(APPEARANCE_STORAGE_KEY, preference);
  applyToDom();
  notify();
}

/** 색상 팔레트를 저장하고 즉시 DOM 에 반영한다. 서버 저장은 호출부 책임. */
export function setThemePalette(palette: ThemePalette) {
  writeStorage(PALETTE_STORAGE_KEY, palette);
  applyToDom();
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 설정 UI 등에서 현재 밝기 모드 선호값을 구독한다. */
export function useAppearancePreference(): AppearancePreference {
  return useSyncExternalStore(subscribe, getAppearancePreference);
}

/** 설정 UI 등에서 현재 색상 팔레트를 구독한다. */
export function useThemePalette(): ThemePalette {
  return useSyncExternalStore(subscribe, getThemePalette);
}

/** system 선호값까지 해석한 최종 다크 여부를 구독한다(네이티브 상태바 동기화 등에 사용). */
export function useIsDarkResolved(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => resolveAppearance(getAppearancePreference()) === "dark",
  );
}

// OS 다크모드 변경: system 선호일 때만 재해석해 반영.
darkQuery.addEventListener("change", () => {
  if (getAppearancePreference() === "system") {
    applyToDom();
    notify();
  }
});

// 같은 브라우저의 다른 탭에서 테마를 바꾼 경우 따라간다.
window.addEventListener("storage", (event) => {
  // 테마 관련 키가 아닌지
  const isUnrelatedKey =
    event.key !== APPEARANCE_STORAGE_KEY && event.key !== PALETTE_STORAGE_KEY;
  if (isUnrelatedKey) return;
  applyToDom();
  notify();
});

// 모듈 로드 시 한 번 동기화한다. data-appearance/data-theme는 index.html의 인라인
// 스크립트가 이미 첫 페인트 전에 반영해두지만(재실행해도 같은 값이라 무해), theme-color
// 메타태그는 그 인라인 스크립트가 건드리지 않아 이 호출이 없으면 설정을 한 번도 안 바꾼
// 세션에서 계속 index.html의 하드코딩값(#4f46e5)으로 남는다.
applyToDom();
